/**
 * Agrupación UI de indicadores BPB por `indicator.category` desde el backend (risk_analysis.risk_indicator / ms-application).
 * Si `category` es null/vacío o no coincide con etiquetas conocidas, se usa el mapa legacy por código BPB.
 *
 * Mantener alineado con bff-moor: src/domain/risk-bpb-category-group.ts
 */

export const GROUP_SITUACION_FINANCIERA = 'SITUACION_FINANCIERA';
export const GROUP_ENDEUDAMIENTO = 'ENDEUDAMIENTO';
export const GROUP_RENTABILIDAD = 'RENTABILIDAD';
export const GROUP_PREDICTOR_ALTMAN = 'PREDICTOR_DE_ALTMAN';
export const GROUP_PREDICTOR_SPRINGATE = 'PREDICTOR_DE_SPRINGATE';
export const GROUP_RATIOS_POST_BALANCE = 'RATIOS_POST_BALANCE';
export const GROUP_OTROS = 'OTROS';
export const GROUP_VENTAS_ACTIVIDAD = 'VENTAS_Y_ACTIVIDAD';
export const GROUP_FLUJO_FONDOS = 'FLUJO_DE_FONDOS';
/** Indicadores con category null/vacío desde el backend — se renderizan como KPI, no como tabla. */
export const GROUP_UNCATEGORIZED = 'UNCATEGORIZED';

export const BPB_CODE_TO_GROUP: Record<string, string> = {
  BPB_001: GROUP_SITUACION_FINANCIERA,
  BPB_002: GROUP_SITUACION_FINANCIERA,
  BPB_007: GROUP_SITUACION_FINANCIERA,
  BPB_008: GROUP_SITUACION_FINANCIERA,
  BPB_009: GROUP_SITUACION_FINANCIERA,
  BPB_003: GROUP_ENDEUDAMIENTO,
  BPB_004: GROUP_ENDEUDAMIENTO,
  BPB_005: GROUP_ENDEUDAMIENTO,
  BPB_006: GROUP_ENDEUDAMIENTO,
  BPB_010: GROUP_ENDEUDAMIENTO,
  BPB_011: GROUP_ENDEUDAMIENTO,
  BPB_012: GROUP_ENDEUDAMIENTO,
  BPB_014: GROUP_ENDEUDAMIENTO,
  BPB_015: GROUP_ENDEUDAMIENTO,
  BPB_016: GROUP_ENDEUDAMIENTO,
  BPB_017: GROUP_ENDEUDAMIENTO,
  BPB_013: GROUP_RENTABILIDAD,
  BPB_018: GROUP_RENTABILIDAD,
  BPB_019: GROUP_RENTABILIDAD,
  BPB_020: GROUP_RENTABILIDAD,
  BPB_021: GROUP_RENTABILIDAD,
  BPB_022: GROUP_PREDICTOR_ALTMAN,
  BPB_023: GROUP_PREDICTOR_ALTMAN,
  BPB_024: GROUP_PREDICTOR_ALTMAN,
  BPB_025: GROUP_PREDICTOR_ALTMAN,
  BPB_026: GROUP_PREDICTOR_ALTMAN,
  BPB_027: GROUP_PREDICTOR_ALTMAN,
  BPB_028: GROUP_SITUACION_FINANCIERA,
  BPB_029: GROUP_ENDEUDAMIENTO,
  BPB_030: GROUP_PREDICTOR_SPRINGATE,
  BPB_031: GROUP_PREDICTOR_SPRINGATE,
  BPB_032: GROUP_PREDICTOR_SPRINGATE,
  BPB_033: GROUP_PREDICTOR_SPRINGATE,
  BPB_034: GROUP_PREDICTOR_SPRINGATE,
  BPB_035: GROUP_SITUACION_FINANCIERA,
  BPB_036: GROUP_SITUACION_FINANCIERA,
  BPB_061: GROUP_VENTAS_ACTIVIDAD,
  BPB_062: GROUP_VENTAS_ACTIVIDAD,
  BPB_063: GROUP_VENTAS_ACTIVIDAD,
  BPB_064: GROUP_ENDEUDAMIENTO,
  BPB_065: GROUP_FLUJO_FONDOS,
  BPB_066: GROUP_FLUJO_FONDOS,
  BPB_069: GROUP_FLUJO_FONDOS,
};

export function normalizeBpbCategoryLabel(raw: string): string {
  return stripDiacritics(raw).toLowerCase().trim().replace(/\s+/g, ' ');
}

function stripDiacritics(input: string): string {
  return input.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function mapFromNormalizedEnumStyle(nUnderscores: string): string | null {
  if (/^sit(uacion)?_financiera$/.test(nUnderscores) || nUnderscores === 'situacionfinanciera') {
    return GROUP_SITUACION_FINANCIERA;
  }
  if (nUnderscores === 'endeudamiento' || nUnderscores === 'apalancamiento') {
    return GROUP_ENDEUDAMIENTO;
  }
  if (nUnderscores === 'rentabilidad') {
    return GROUP_RENTABILIDAD;
  }
  if (
    nUnderscores.includes('altman') ||
    nUnderscores === 'predictor_de_altman' ||
    nUnderscores === 'predictordealman'
  ) {
    return GROUP_PREDICTOR_ALTMAN;
  }
  if (
    nUnderscores.includes('springate') ||
    nUnderscores === 'predictor_de_springate' ||
    nUnderscores === 'predictordespringate'
  ) {
    return GROUP_PREDICTOR_SPRINGATE;
  }
  return null;
}

export function resolveGroupFromRiskCategoryLabel(
  category: string | null | undefined
): string | null {
  if (category === undefined || category === null) return null;
  const trimmed = String(category).trim();
  if (!trimmed) return null;

  const upperCompressed = trimmed.toUpperCase().replace(/\s+/g, '');
  const n = normalizeBpbCategoryLabel(trimmed);
  const nUnder = n.replace(/\s+/g, '_');

  const enumHit = mapFromNormalizedEnumStyle(nUnder);
  if (enumHit) return enumHit;

  if (
    stripDiacritics(trimmed).toUpperCase().replace(/\s+/g, '_').includes('SITUACION_FINANCIERA')
  ) {
    return GROUP_SITUACION_FINANCIERA;
  }
  if (upperCompressed.includes('SITUACIONFINANCIERA') || /\bsituacion\s+financiera\b/.test(n)) {
    return GROUP_SITUACION_FINANCIERA;
  }
  if (/\bliquidez\b/.test(n) || /\b(capital\s+de\s+trabajo|volatilidad)\b/.test(n)) {
    return GROUP_SITUACION_FINANCIERA;
  }

  if (/\b(apalancamiento|endeudamiento)\b/.test(n)) {
    return GROUP_ENDEUDAMIENTO;
  }

  if (/\brentabilidad\b/.test(n)) {
    return GROUP_RENTABILIDAD;
  }

  if (/\bventas\b.*\bactividad\b/.test(n) || n === 'ventas y actividad') {
    return GROUP_VENTAS_ACTIVIDAD;
  }

  if (/\bflujo\b.*\bfondos\b/.test(n) || n === 'flujo de fondos') {
    return GROUP_FLUJO_FONDOS;
  }

  if (/\baltman\b/.test(n) || /\bpredictor(\s|_)*de(\s|_)*altman\b/.test(n)) {
    return GROUP_PREDICTOR_ALTMAN;
  }

  if (/\bspringate\b/.test(n) || /\bpredictor(\s|_)*de(\s|_)*springate\b/.test(n)) {
    return GROUP_PREDICTOR_SPRINGATE;
  }

  if (trimmed.toLowerCase() === 'otros') {
    return GROUP_OTROS;
  }

  if (/ratios?\s+post\s+balance/i.test(trimmed)) {
    return GROUP_RATIOS_POST_BALANCE;
  }

  return null;
}

export function resolveBpbIndicatorGroup(
  category: string | null | undefined,
  code: string
): string {
  if (category == null || String(category).trim() === '') {
    return GROUP_UNCATEGORIZED;
  }
  const fromCatalog = resolveGroupFromRiskCategoryLabel(category);
  if (fromCatalog) return fromCatalog;
  return BPB_CODE_TO_GROUP[code] ?? GROUP_SITUACION_FINANCIERA;
}

export const GROUP_ORDER = [
  GROUP_SITUACION_FINANCIERA,
  GROUP_VENTAS_ACTIVIDAD,
  GROUP_ENDEUDAMIENTO,
  GROUP_FLUJO_FONDOS,
  GROUP_RENTABILIDAD,
  GROUP_RATIOS_POST_BALANCE,
  GROUP_PREDICTOR_ALTMAN,
  GROUP_PREDICTOR_SPRINGATE,
  GROUP_OTROS,
] as const;

export const GROUP_TITLES: Record<string, string> = {
  [GROUP_SITUACION_FINANCIERA]: 'Situación Financiera',
  [GROUP_VENTAS_ACTIVIDAD]: 'Ventas y Actividad',
  [GROUP_ENDEUDAMIENTO]: 'Endeudamiento',
  [GROUP_FLUJO_FONDOS]: 'Flujo de Fondos',
  [GROUP_RENTABILIDAD]: 'Rentabilidad',
  [GROUP_PREDICTOR_ALTMAN]: 'Predictor de Altman',
  [GROUP_PREDICTOR_SPRINGATE]: 'Predictor de Springate',
  [GROUP_RATIOS_POST_BALANCE]: 'Ratios Post Balance',
  [GROUP_OTROS]: 'Otros',
};

/** Sort key for BPB_NNN codes (shared by legacy + Atlantic grouping). */
export function bpbCodeToSortKey(code: string): number {
  const m = code.match(/^BPB_(\d+)$/);
  return m ? parseInt(m[1], 10) : 999;
}

export function compareBpbCodesByOrder(
  codeA: string,
  codeB: string,
  indicatorOrder?: Record<string, number>,
): number {
  const orderMap = indicatorOrder ?? {};
  const orderA =
    typeof orderMap[codeA] === 'number' && !Number.isNaN(orderMap[codeA])
      ? orderMap[codeA]
      : Number.POSITIVE_INFINITY;
  const orderB =
    typeof orderMap[codeB] === 'number' && !Number.isNaN(orderMap[codeB])
      ? orderMap[codeB]
      : Number.POSITIVE_INFINITY;
  if (orderA !== orderB) return orderA - orderB;
  return bpbCodeToSortKey(codeA) - bpbCodeToSortKey(codeB);
}
